import { User } from "./User";
import { Company } from "./Company";
import { ProjectUser } from "./ProjectUser";
import { SignatoryClient } from "./SignatoryClient";

/**
 * Project Domain enum
 */
export enum ProjectDomain {
  Data = "Data",
  Insight = "Insight",
  Both = "Both"
}

/**
 * Mission Type enum
 */
export enum MissionType {
  BusinessExpert = "BusinessExpert",
  ManagementTransition = "ManagementTransition",
  TalentOnDemand = "TalentOnDemand",
  TalentPoolOnDemand = "TalentPoolOnDemand"
}

/**
 * Project Type enum
 */
export enum ProjectType {
  FixedPrice = "FixedPrice",
  TimeAndMaterial = "TimeAndMaterial"
}

/**
 * Duration Type enum
 */
export enum DurationType {
  Weeks = "Weeks",
  Months = "Months"
}

/**
 * Project Location enum
 */
export enum ProjectLocation {
  OnSite = "OnSite",
  Remote = "Remote",
  Hybrid = "Hybrid",
  NoPreference = "NoPreference"
}

/**
 * Project Status enum
 */
export enum ProjectStatus {
  Draft = "Draft",
  Submitted = "Submitted",
  Recruitment = "Recruitment",
  Identified = "Identified",
  Proposed = "Proposed",
  InProgress = "InProgress",
  Delivered = "Delivered",
  Completed = "Completed",
  Closed = "Closed",
  Archived = "Archived"
}

/**
 * Project interface
 */
export interface Project {
  id: number;
  projectNumber: string;
  name: string;
  domain: ProjectDomain;
  missionType?: MissionType;
  type: ProjectType;
  companyId?: number;
  company?: Company;
  objectives?: string;
  presentationAndNeeds?: string;
  talentHardSkills?: string;
  talentSoftSkills?: string;
  budget?: number;
  dailyRate?: number;
  duration?: number;
  durationType?: DurationType;
  fullTime?: boolean;
  daysPerPeriod?: number;
  workingDays?: number;
  location: ProjectLocation;
  hybridDetails?: string;
  teamIntegration?: boolean;
  desiredStartDate?: string;
  startDate?: string;
  endDate?: string;
  feePercentage?: number;
  externalCostsFeePercentage?: number;
  expensesFeePercentage?: number;
  processingFees?: number;
  internalNotes?: string;
  status: ProjectStatus;
  createdById: number;
  createdBy?: User;
  createdAt: string;
  updatedAt?: string;
  associatedUsers?: ProjectUser[];
  signatoryClient?: SignatoryClient;
}


/**
 * Project Create Request DTO
 */
export interface ProjectCreateRequest {
  name: string;
  domain?: ProjectDomain;
  missionType?: MissionType;
  type?: ProjectType;
  companyId?: number;
  objectives?: string;
  presentationAndNeeds?: string;
  talentHardSkills?: string;
  talentSoftSkills?: string;
  budget?: number;
  dailyRate?: number;
  duration?: number;
  durationType?: DurationType;
  fullTime?: boolean;
  daysPerPeriod?: number;
  location?: ProjectLocation;
  hybridDetails?: string;
  teamIntegration?: boolean;
  desiredStartDate?: string;
  endDate?: string;
  internalNotes?: string;
  feePercentage?: number;
  externalCostsFeePercentage?: number;
  expensesFeePercentage?: number;
  processingFees?: number;
}

/**
 * Project Update Request DTO
 */
export interface ProjectUpdateRequest {
  name?: string;
  domain?: ProjectDomain;
  missionType?: MissionType;
  type?: ProjectType;
  companyId?: number;
  objectives?: string;
  presentationAndNeeds?: string;
  talentHardSkills?: string;
  talentSoftSkills?: string;
  budget?: number;
  dailyRate?: number;
  duration?: number;
  durationType?: DurationType;
  fullTime?: boolean;
  daysPerPeriod?: number;
  workingDays?: number;
  location?: ProjectLocation;
  hybridDetails?: string;
  teamIntegration?: boolean;
  desiredStartDate?: string;
  startDate?: string;
  endDate?: string;
  feePercentage?: number;
  externalCostsFeePercentage?: number;
  expensesFeePercentage?: number;
  processingFees?: number;
  internalNotes?: string;
  status?: ProjectStatus;
}

/**
 * Project Status Update Request DTO
 */
export interface ProjectStatusUpdateRequest {
  status: ProjectStatus;
  comments?: string;
}

